import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { setHomeownerPassword, updateHomeownerClient } from '../utils/homeownerApi';
import { authConfirmUrl } from '../shared/siteUrl';
import { WRAP_TEXT } from '../shared/wrapText';
import { useToast } from './Toast';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AccountSignIn = ({ mode, email, colors, onEmailSaved }) => {
  const { showToast } = useToast();
  const [nextEmail, setNextEmail] = useState(email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const buttonStyle = {
    background: '#59c4c4',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    fontWeight: 700,
    cursor: 'pointer',
  };
  const fieldStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: `2px solid ${colors.border}`,
    background: colors.surfaceAlt,
    color: colors.text,
    marginBottom: '0.75rem',
  };

  const saveEmail = async (event) => {
    event.preventDefault();
    const value = nextEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      showToast('Enter a valid email.', 'error');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'agent') {
        const { error } = await supabase.auth.updateUser(
          { email: value },
          { emailRedirectTo: authConfirmUrl(window.location) }
        );
        if (error) throw error;
        showToast('Check your inbox for the new sign-in email.', 'success');
      } else {
        const result = await updateHomeownerClient({ email: value });
        if (onEmailSaved) onEmailSaved((result && result.client && result.client.email) || value);
        setEditingEmail(false);
        showToast('Email updated.', 'success');
      }
    } catch (error) {
      showToast(error.message || 'Could not update the email.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      showToast('Use a password of at least 8 characters.', 'error');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'agent') {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
      } else {
        await setHomeownerPassword(password, currentPassword);
      }
      setPassword('');
      setCurrentPassword('');
      setPasswordNotice('Password updated.');
      showToast('Password updated.', 'success');
    } catch (error) {
      showToast(error.message || 'Could not update the password.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {mode === 'homeowner' && !editingEmail ? (
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'block', color: colors.text, fontWeight: 600, marginBottom: '0.35rem' }}>
            Email
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{
              flex: '1 1 12rem',
              color: colors.text,
              lineHeight: 1.45,
              whiteSpace: 'normal',
              ...WRAP_TEXT,
            }}>
              {email}
            </span>
            <button
              type="button"
              onClick={() => {
                setNextEmail(email || '');
                setEditingEmail(true);
              }}
              style={{ ...buttonStyle, flex: '0 0 auto' }}
            >
              Change email
            </button>
          </div>
        </div>
      ) : (
      <form onSubmit={saveEmail}>
        <label htmlFor="account-sign-in-email" style={{ display: 'block', color: colors.text, fontWeight: 600, marginBottom: '0.35rem' }}>
          {mode === 'agent' ? 'Sign-in email' : 'Email'}
        </label>
        <input
          id="account-sign-in-email"
          type="email"
          value={nextEmail}
          onChange={(event) => setNextEmail(event.target.value)}
          autoComplete="email"
          style={fieldStyle}
        />
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <button type="submit" disabled={busy} style={buttonStyle}>
            Change email
          </button>
          {mode === 'homeowner' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditingEmail(false)}
              style={{ ...buttonStyle, background: 'transparent', color: colors.text, border: `1px solid ${colors.border}` }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
      )}
      <form onSubmit={savePassword}>
        <label htmlFor="account-new-password" style={{ display: 'block', color: colors.text, fontWeight: 600, marginBottom: '0.35rem' }}>
          Change password
        </label>
        {mode === 'homeowner' && (
          <input
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            placeholder="Current password"
            autoComplete="current-password"
            style={fieldStyle}
          />
        )}
        <input
          id="account-new-password"
          type="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            setPasswordNotice('');
          }}
          placeholder="New password (8+ characters)"
          autoComplete="new-password"
          minLength={8}
          style={fieldStyle}
        />
        <button type="submit" disabled={busy} style={buttonStyle}>Change password</button>
        {passwordNotice && (
          <p role="status" style={{ color: '#059669', margin: '0.75rem 0 0', fontWeight: 600 }}>
            {passwordNotice}
          </p>
        )}
      </form>
    </div>
  );
};

export default AccountSignIn;
